import type {
  ICollectionDefinitionBuilder,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';
import Service, { service } from '@ember/service';
import type RouterService from '@ember/routing/router-service';
import type { IntlService } from 'ember-intl';
import type StoreService from './_store';
import type CustomFieldDefinitionsService from './custom-field-definitions-service';
import type LabelService from './label-service';
import type SessionUserService from './session-user';

import InputFilter from 'volta/components/filters/input-filter';
import TokenFilter from 'volta/components/filters/token-filter';

import { ReportResourceName } from 'volta/models/report';
import { columnsToProps, columnsToSortOptions } from 'volta/models/collection-view';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import { indexByFn } from 'volta/utils/array-utils';
import { collectionFiltersFromQueryParams } from 'volta/utils/filters-utils';

export default class ReportCollectionDefinitionBuilder
  extends Service
  implements ICollectionDefinitionBuilder
{
  @service store!: StoreService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service sessionUser!: SessionUserService;
  @service router!: RouterService;

  SORT_OPTIONS: ICollectionSortOption[] = [
    { value: 'name', label: 'name', origin: 'name' },
    { value: 'createdAt', label: 'createdAt', origin: 'createdAt' },
    { value: 'updatedAt', label: 'updatedAt', origin: 'updatedAt' }
  ];

  buildColumns() {
    const { intl } = this;
    return [
      {
        columnName: intl.t('name'),
        sortKey: 'name',
        valuePath: 'name'
      },
      {
        columnName: intl.t('project'),
        sortKey: 'projectName',
        valuePath: 'project'
      },
      {
        columnName: intl.t('workbook'),
        sortKey: 'workbookName',
        valuePath: 'workbook'
      }
    ];
  }

  buildFilters(queryParamsValues: TQueryParams): TFilterDefinition[] {
    const { filters } = queryParamsValues;
    const cvFilters = indexByFn(
      collectionFiltersFromQueryParams(filters as string),
      (f) => f.property
    );
    const projectNames = cvFilters['project']?.values.join(',');
    return [
      {
        queryParam: 'project',
        apiParam: 'projectName',
        comparators: ['eq'],
        component: TokenFilter as TBaseFilterComponent,
        componentArgs: {
          searchFn: (query: IJsonApiQuery) => {
            return this.store.customQuery('reports', 'projectNames', undefined, {
              ...query
            });
          }
        }
      },
      {
        queryParam: 'workbook',
        apiParam: 'workbookName',
        comparators: ['in'],
        component: TokenFilter as TBaseFilterComponent,
        componentArgs: {
          searchFn: (query: IJsonApiQuery) => {
            return this.store.customQuery('reports', 'workbookNames', undefined, {
              ...query,
              projectNames
            });
          }
        }
      },
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        hidden: true,
        // @ts-ignore
        component: InputFilter,
        componentArgs: {}
      }
    ];
  }

  buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const properties = columnsToProps(columns, (c: IBvTableColumn) => !c.disabled);
    return {
      name: this.intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: this.intl.t(ReportResourceName.plural)
      }),
      description: this.intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: this.intl.t(ReportResourceName.plural)
      }),
      collectionType: 'LIST',
      entityType: ReportResourceName.plural,
      properties,
      sortBy: [{ property: 'name', asc: true }],
      createdBy: this.sessionUser.userId,
      filters: []
    };
  }

  build(queryParams: TQueryParams, sorts: ICollectionSortOption[] = []) {
    const filterDefinitions = this.buildFilters(queryParams);
    const columns = this.buildColumns();
    const defaultView = this.buildDefaultView(columns);

    const view: ICollectionViewDefinition = {
      resourceName: ReportResourceName,
      sortLimit: 0,
      sorts: columnsToSortOptions(columns).concat(sorts) as ICollectionSortOption[],
      properties: columnsToProps(columns),
      filters: filterDefinitions,
      definitions: columns,
      types: ['TABLE'],
      defaultView
    };

    return view;
  }
}

declare module '@ember/service' {
  interface Registry {
    'collection-builder-reports': ReportCollectionDefinitionBuilder;
  }
}
